import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Settings, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../ui/dropdown-menu';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../contexts/SiteContext';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { settings, pages } = useSite();
  const location = useLocation();

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // Build ordered nav items: plain links, one-level groups, and two-level group ▸ section ▸ pages.
  const menuPages = Array.isArray(pages) ? pages : [];
  const pageItems = [];
  const groupPos = {};
  const sectionPos = {};
  menuPages.forEach((page) => {
    const group = (page.menu_group || '').trim();
    const section = (page.menu_section || '').trim();
    const link = { type: 'link', href: `/page/${page.slug}`, label: page.title };
    if (!group) {
      pageItems.push(link);
      return;
    }
    if (groupPos[group] === undefined) {
      groupPos[group] = pageItems.length;
      pageItems.push({ type: 'group', label: group, children: [] });
    }
    const groupItem = pageItems[groupPos[group]];
    if (!section) {
      groupItem.children.push(link);
      return;
    }
    const key = `${group}||${section}`;
    if (sectionPos[key] === undefined) {
      sectionPos[key] = groupItem.children.length;
      groupItem.children.push({ type: 'section', label: section, children: [] });
    }
    groupItem.children[sectionPos[key]].children.push(link);
  });

  // Collect every href under a nav item (for active-state highlighting).
  const collectHrefs = (item) => {
    if (item.type === 'link') return [item.href];
    if (item.children) return item.children.flatMap(collectHrefs);
    return [];
  };
  const slug = (s) => s.toLowerCase().replace(/\s+/g, '-');

  const navItems = [
    { type: 'link', href: '/', label: 'Home' },
    ...pageItems,
    { type: 'link', href: '/contact', label: 'Contact' },
  ];

  const linkClass = (active) =>
    `text-sm font-medium transition-colors ${
      theme === 'dark'
        ? `hover:text-primary ${active ? 'text-primary' : 'text-muted-foreground'}`
        : `hover:text-foreground ${active ? 'text-foreground font-semibold' : 'text-muted-foreground'}`
    }`;

  return (
    <header
      className={`sticky top-0 z-50 glass border-b ${
        theme === 'dark'
          ? 'bg-zinc-950/60 border-zinc-800'
          : 'bg-white/70 border-zinc-200'
      }`}
    >
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link
            to="/"
            className="font-bold text-xl tracking-tight hover:text-primary transition-colors"
            data-testid="site-logo"
          >
            {settings.site_name}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
            {navItems.map((item) => {
              if (item.type === 'group') {
                const groupActive = collectHrefs(item).some((h) => isActive(h));
                return (
                  <DropdownMenu key={`group-${item.label}`}>
                    <DropdownMenuTrigger
                      className={`flex items-center gap-1 outline-none ${linkClass(groupActive)}`}
                      data-testid={`nav-group-${slug(item.label)}`}
                    >
                      {item.label}
                      <ChevronDown className="h-3.5 w-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[12rem]">
                      {item.children.map((child) => {
                        if (child.type === 'section') {
                          const sectionActive = child.children.some((c) => isActive(c.href));
                          return (
                            <DropdownMenuSub key={`section-${child.label}`}>
                              <DropdownMenuSubTrigger
                                data-testid={`nav-section-${slug(child.label)}`}
                                className={`focus:text-accent-foreground data-[state=open]:text-accent-foreground ${sectionActive ? 'font-semibold' : ''}`}
                              >
                                {child.label}
                              </DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent className="min-w-[12rem]">
                                  {child.children.map((leaf) => (
                                    <DropdownMenuItem key={leaf.href} asChild>
                                      <Link
                                        to={leaf.href}
                                        data-testid={`nav-link-${slug(leaf.label)}`}
                                        className={isActive(leaf.href) ? 'font-semibold' : ''}
                                      >
                                        {leaf.label}
                                      </Link>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                          );
                        }
                        return (
                          <DropdownMenuItem key={child.href} asChild>
                            <Link
                              to={child.href}
                              data-testid={`nav-link-${slug(child.label)}`}
                              className={isActive(child.href) ? 'font-semibold' : ''}
                            >
                              {child.label}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-link-${slug(item.label)}`}
                  className={linkClass(isActive(item.href))}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Admin Link - shown when logged in */}
            {user && (
              <Link to="/admin" data-testid="admin-link">
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Admin
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="sm:hidden"
                  aria-label="Admin dashboard"
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              data-testid="theme-toggle-btn"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav
          className={`md:hidden border-t ${
            theme === 'dark' ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'
          }`}
          aria-label="Mobile navigation"
        >
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => {
              if (item.type === 'group') {
                return (
                  <div key={`m-group-${item.label}`} className="pt-2">
                    <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {item.label}
                    </p>
                    {item.children.map((child) => {
                      if (child.type === 'section') {
                        return (
                          <div key={`m-section-${child.label}`} className="mt-1">
                            <p className="px-4 py-1 pl-8 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                              {child.label}
                            </p>
                            {child.children.map((leaf) => (
                              <Link
                                key={leaf.href}
                                to={leaf.href}
                                data-testid={`mobile-nav-link-${slug(leaf.label)}`}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`block pl-12 pr-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                  isActive(leaf.href) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                                }`}
                              >
                                {leaf.label}
                              </Link>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          data-testid={`mobile-nav-link-${slug(child.label)}`}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block pl-8 pr-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive(child.href) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          }`}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                );
              }
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`mobile-nav-link-${slug(item.label)}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            {user && (
              <Link
                to="/admin"
                data-testid="mobile-admin-link"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-md text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                Admin Dashboard
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
